/**
 * Multi-Input Sensor Template
 * For devices with multiple digital input channels (read-only devices)
 */

/**
 * Create inputState condition for input state checking
 */
export function createInputStateCondition() {
	return {
		label: 'Input State',
		description: 'Check the current state of an input channel',
		channels: ['input:0', 'input:1', 'input:2', 'input:3'],
		parameters: [
			{
				name: 'state',
				label: 'Expected State',
				type: 'select',
				options: [
					{ value: true, label: 'Active (High)' },
					{ value: false, label: 'Inactive (Low)' },
				],
				description: 'Expected input state',
			},
		],
		evaluateCondition: (deviceState, channel, params) => {
			const inputId = parseInt(channel.split(':')[1]);
			const inputState = deviceState.status?.['input:' + inputId];
			return inputState?.state === params.state;
		},
	};
}

/**
 * Create allInputsActive condition
 */
export function createAllInputsActiveCondition() {
	return {
		label: 'All Inputs Active',
		description: 'Check if all input channels are active',
		channels: ['input:0', 'input:1', 'input:2', 'input:3'],
		parameters: [],
		evaluateCondition: (deviceState) => {
			for (let i = 0; i < 4; i++) {
				const inputState = deviceState.status?.['input:' + i];
				if (!inputState?.state) return false;
			}
			return true;
		},
	};
}

/**
 * Create anyInputActive condition
 */
export function createAnyInputActiveCondition() {
	return {
		label: 'Any Input Active',
		description: 'Check if any input channel is active',
		channels: ['input:0', 'input:1', 'input:2', 'input:3'],
		parameters: [],
		evaluateCondition: (deviceState) => {
			for (let i = 0; i < 4; i++) {
				const inputState = deviceState.status?.['input:' + i];
				if (inputState?.state) return true;
			}
			return false;
		},
	};
}

/**
 * Create inputCount condition for active input counting
 */
export function createInputCountCondition() {
	return {
		label: 'Active Input Count',
		description: 'Check how many inputs are currently active',
		channels: ['input:0', 'input:1', 'input:2', 'input:3'],
		parameters: [
			{
				name: 'operator',
				label: 'Comparison',
				type: 'select',
				options: [
					{ value: 'eq', label: 'Equals' },
					{ value: 'gt', label: 'Greater than' },
					{ value: 'gte', label: 'Greater or equal' },
					{ value: 'lt', label: 'Less than' },
					{ value: 'lte', label: 'Less or equal' },
				],
				description: 'Comparison operator',
			},
			{
				name: 'count',
				label: 'Input Count',
				type: 'number',
				min: 0,
				max: 4,
				description: 'Target number of active inputs',
			},
		],
		evaluateCondition: (deviceState, channel, params) => {
			let activeCount = 0;
			for (let i = 0; i < 4; i++) {
				const inputState = deviceState.status?.['input:' + i];
				if (inputState?.state) activeCount++;
			}

			switch (params.operator) {
				case 'eq':
					return activeCount === params.count;
				case 'gt':
					return activeCount > params.count;
				case 'gte':
					return activeCount >= params.count;
				case 'lt':
					return activeCount < params.count;
				case 'lte':
					return activeCount <= params.count;
				default:
					return false;
			}
		},
	};
}

/**
 * Create inputChanged trigger for input state change detection
 */
export function createInputChangedTrigger() {
	return {
		label: 'Input State Changed',
		description: 'Triggered when an input changes state',
		channels: ['input:0', 'input:1', 'input:2', 'input:3'],
		parameters: [
			{
				name: 'to',
				label: 'Changed To',
				type: 'select',
				options: [
					{ value: 'any', label: 'Any state' },
					{ value: true, label: 'Became active' },
					{ value: false, label: 'Became inactive' },
				],
				description: 'State change direction',
				optional: true,
			},
		],
		detectTrigger: (oldState, newState, channel, params) => {
			const inputId = parseInt(channel.split(':')[1]);
			const oldInput = oldState?.status?.['input:' + inputId]?.state;
			const newInput = newState?.status?.['input:' + inputId]?.state;

			if (oldInput === newInput) return false;

			if (params.to === 'any' || !params.to) return true;
			return newInput === params.to;
		},
	};
}

/**
 * Create motionDetected trigger
 */
export function createMotionDetectedTrigger() {
	return {
		label: 'Motion Detected',
		description: 'Triggered when motion sensor input becomes active',
		channels: ['input:0', 'input:1', 'input:2', 'input:3'],
		parameters: [
			{
				name: 'debounceTime',
				label: 'Debounce Time',
				type: 'number',
				min: 100,
				max: 10000,
				unit: 'ms',
				description: 'Minimum time between triggers (default: 1000ms)',
				optional: true,
				defaultValue: 1000,
			},
		],
		detectTrigger: (oldState, newState, channel, params) => {
			const inputId = parseInt(channel.split(':')[1]);
			const oldInput = oldState?.status?.['input:' + inputId]?.state;
			const newInput = newState?.status?.['input:' + inputId]?.state;

			// Trigger only when input goes from inactive to active (motion detected)
			return oldInput === false && newInput === true;
		},
	};
}

/**
 * Create doorOpened trigger
 */
export function createDoorOpenedTrigger() {
	return {
		label: 'Door/Window Opened',
		description: 'Triggered when door/window sensor opens',
		channels: ['input:0', 'input:1', 'input:2', 'input:3'],
		parameters: [],
		detectTrigger: (oldState, newState, channel, params) => {
			const inputId = parseInt(channel.split(':')[1]);
			const oldInput = oldState?.status?.['input:' + inputId]?.state;
			const newInput = newState?.status?.['input:' + inputId]?.state;

			// Trigger when door/window opens (contact broken)
			return oldInput === true && newInput === false;
		},
	};
}

/**
 * Create doorClosed trigger
 */
export function createDoorClosedTrigger() {
	return {
		label: 'Door/Window Closed',
		description: 'Triggered when door/window sensor closes',
		channels: ['input:0', 'input:1', 'input:2', 'input:3'],
		parameters: [],
		detectTrigger: (oldState, newState, channel, params) => {
			const inputId = parseInt(channel.split(':')[1]);
			const oldInput = oldState?.status?.['input:' + inputId]?.state;
			const newInput = newState?.status?.['input:' + inputId]?.state;

			// Trigger when door/window closes (contact made)
			return oldInput === false && newInput === true;
		},
	};
}

/**
 * Create multipleInputsChanged trigger
 */
export function createMultipleInputsChangedTrigger() {
	return {
		label: 'Multiple Inputs Changed',
		description: 'Triggered when multiple inputs change simultaneously',
		channels: ['input:0', 'input:1', 'input:2', 'input:3'],
		parameters: [
			{
				name: 'minimumInputs',
				label: 'Minimum Changed Inputs',
				type: 'number',
				min: 2,
				max: 4,
				description: 'Minimum number of inputs that must change simultaneously',
				optional: true,
				defaultValue: 2,
			},
		],
		detectTrigger: (oldState, newState, channel, params) => {
			let changedCount = 0;

			for (let i = 0; i < 4; i++) {
				const oldInput = oldState?.status?.['input:' + i]?.state;
				const newInput = newState?.status?.['input:' + i]?.state;

				if (oldInput !== newInput) {
					changedCount++;
				}
			}

			const minimumInputs = params.minimumInputs || 2;
			return changedCount >= minimumInputs;
		},
	};
}

/**
 * Create multi-input sensor template
 * @returns {Object} Scene capabilities for multi-input sensor
 */
export function createMultiInputSensorTemplate() {
	return {
		deviceType: 'multi_input_sensor',
		channels: ['input:0', 'input:1', 'input:2', 'input:3'],

		actions: {
			// Input sensors are read-only devices, no actions available
			// Actions would be handled by connected devices based on sensor triggers
		},

		conditions: {
			inputState: createInputStateCondition(),
			allInputsActive: createAllInputsActiveCondition(),
			anyInputActive: createAnyInputActiveCondition(),
			inputCount: createInputCountCondition(),
		},

		triggers: {
			inputChanged: createInputChangedTrigger(),
			motionDetected: createMotionDetectedTrigger(),
			doorOpened: createDoorOpenedTrigger(),
			doorClosed: createDoorClosedTrigger(),
			multipleInputsChanged: createMultipleInputsChangedTrigger(),
		},
	};
}
